/**
 * FeedbackService – sends feedback entries to the Reports API and manages
 * the offline queue for entries that could not be sent immediately.
 *
 * Singleton pattern consistent with the rest of the codebase (CartService,
 * AuthService, ApiClient). Uses the existing ApiClient for HTTP requests
 * (automatic JWT auth, CSRF, token refresh) and the existing showToast()
 * for user notifications.
 *
 * Validates: Requirements 5.1, 5.2, 5.3, 5.4, 5.5, 6.1, 6.2, 6.3, 6.4,
 *            6.5, 6.6, 7.1, 7.2, 8.1, 8.2, 8.3, 8.4
 */

import type {
  FeedbackEntry,
  FeedbackUserInput,
  FeedbackSubmitResult,
} from '../../shared/models/feedback-entry.model';
import { collectContext } from '../../shared/utils/context-collector';
import { apiClient, ApiError } from '../../shared/services/api-client';
import { showToast } from '../../shared/components/toast';
import { eventBus } from '../../infrastructure/event-bus';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** localStorage key for the offline feedback queue. */
const FEEDBACK_QUEUE_KEY = 'snaphub_feedback_queue';

// ---------------------------------------------------------------------------
// Toast messages (German)
// ---------------------------------------------------------------------------

const TOAST_SUCCESS = 'Feedback erfolgreich gesendet';
const TOAST_VALIDATION_ERROR =
  'Feedback konnte nicht gesendet werden. Bitte überprüfen Sie Ihre Eingaben.';
const TOAST_QUEUED =
  'Feedback wird gesendet, sobald Sie wieder online sind';

// ---------------------------------------------------------------------------
// FeedbackService class
// ---------------------------------------------------------------------------

export class FeedbackService {
  private onlineHandler: (() => void) | null = null;

  // -- Public API -----------------------------------------------------------

  /**
   * Submit a feedback entry to the Reports API.
   *
   * Collects context data automatically, builds the full FeedbackEntry,
   * and attempts to POST it. On network/server errors the entry is
   * enqueued for later synchronisation.
   */
  async submitFeedback(input: FeedbackUserInput): Promise<FeedbackSubmitResult> {
    const context = collectContext();

    // Build the FeedbackEntry – omit feature_key when undefined so
    // JSON.stringify will not include it (Req 8.4).
    const entry: FeedbackEntry = {
      id: context.id,
      message: input.message,
      category: input.category,
      page_url: context.page_url || window.location.hash || '#/',
      page_name: context.page_name || 'SnapHub',
      user_id: context.user_id,
      app_version: context.app_version,
      device_type: context.device_type,
      browser: context.browser,
      status: context.status,
    };

    if (input.feature_key !== undefined) {
      entry.feature_key = input.feature_key;
    }

    // Build the API payload including created_at (required by the API)
    // Note: id is NOT sent – the API generates its own record ID.
    const payload: Record<string, unknown> = {
      message: entry.message,
      category: entry.category,
      page_url: entry.page_url,
      page_name: entry.page_name,
      user_id: entry.user_id,
      app_version: entry.app_version,
      device_type: entry.device_type,
      browser: entry.browser,
      status: entry.status,
      created_at: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
    };

    if (entry.feature_key !== undefined) {
      payload.feature_key = entry.feature_key;
    }

    try {
      await apiClient.post('/api/feedback-api', payload);

      // 201 Created – success
      showToast(TOAST_SUCCESS, 'success');
      eventBus.emit('feedback-submitted', { id: entry.id });

      return { success: true, queued: false };
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 400) {
        // Validation error from the API
        showToast(TOAST_VALIDATION_ERROR, 'error');
        return { success: false, queued: false, error: error.message };
      }

      // Network error (status 0) or server error (500) → enqueue
      this.enqueue(entry);
      showToast(TOAST_QUEUED, 'success');

      return { success: true, queued: true };
    }
  }

  /**
   * Synchronise all entries from the offline queue.
   *
   * Sends entries sequentially. On success (201) the entry is removed
   * from the queue. On any error the sync is aborted and remaining
   * entries stay in the queue for the next attempt.
   */
  async syncOfflineQueue(): Promise<void> {
    const queue = this.readQueue();
    if (queue.length === 0) return;

    for (let i = 0; i < queue.length; i++) {
      try {
        const entry = queue[i];
        const syncPayload: Record<string, unknown> = {
          message: entry.message,
          category: entry.category,
          page_url: entry.page_url,
          page_name: entry.page_name,
          user_id: entry.user_id,
          app_version: entry.app_version,
          device_type: entry.device_type,
          browser: entry.browser,
          status: entry.status,
          created_at: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
        };
        if (entry.feature_key !== undefined) {
          syncPayload.feature_key = entry.feature_key;
        }
        await apiClient.post('/api/feedback-api', syncPayload);

        // Success – remove this entry and persist
        queue.splice(i, 1);
        this.writeQueue(queue);
        i--; // adjust index after splice

        showToast(TOAST_SUCCESS, 'success');
      } catch {
        // Error – abort sync, leave remaining entries in queue
        return;
      }
    }
  }

  /**
   * Returns the number of entries currently in the offline queue.
   */
  getOfflineQueueCount(): number {
    return this.readQueue().length;
  }

  /**
   * Start listening for the browser `online` event to trigger
   * automatic queue synchronisation.
   */
  initOnlineListener(): void {
    this.destroyOnlineListener();
    this.onlineHandler = () => {
      void this.syncOfflineQueue();
    };
    window.addEventListener('online', this.onlineHandler);
  }

  /**
   * Remove the `online` event listener (for tests / cleanup).
   */
  destroyOnlineListener(): void {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
  }

  // -- Private helpers ------------------------------------------------------

  /**
   * Read the offline queue from localStorage.
   * Returns an empty array on parse errors or missing data.
   */
  private readQueue(): FeedbackEntry[] {
    try {
      const raw = localStorage.getItem(FEEDBACK_QUEUE_KEY);
      if (!raw) return [];
      return JSON.parse(raw) as FeedbackEntry[];
    } catch {
      return [];
    }
  }

  /**
   * Write the offline queue to localStorage.
   * Silently catches quota-exceeded or other storage errors.
   */
  private writeQueue(queue: FeedbackEntry[]): void {
    try {
      localStorage.setItem(FEEDBACK_QUEUE_KEY, JSON.stringify(queue));
    } catch {
      // localStorage full or unavailable – nothing we can do
    }
  }

  /**
   * Add a single entry to the end of the offline queue.
   */
  private enqueue(entry: FeedbackEntry): void {
    const queue = this.readQueue();
    queue.push(entry);
    this.writeQueue(queue);
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

/** Global FeedbackService instance shared across the application. */
export const feedbackService = new FeedbackService();
